/**
 * How a clinic issues its invoices: which provider, on whose account, under what series.
 *
 * Unlike `reminderSettings`, a clinic that has never opened this screen is genuinely
 * **unconfigured** rather than running on a working default -- there is no sensible default
 * for somebody else's fiscal code. The API still answers with a complete object rather than
 * nulls, so the form has no empty state to render; it just opens on `provider: 'NONE'` and
 * `enabled: false`, which is the truth about that clinic.
 *
 * The API secret is the one thing the response never carries. `apiSecretSet` says whether one
 * is stored and nothing else, and a request that leaves `apiSecret` null keeps what is
 * already there. That is what makes a write-only field workable at all: a screen that saves
 * what it was shown cannot send back a value it was never given.
 */

/** What a provider is called, and what to call it in a dropdown. */
export interface IAvailableProvider {
    key: string;
    displayName: string;
}

/** What GET /invoicing-settings returns. */
export interface IInvoicingSettings {
    provider: string;
    enabled: boolean;
    apiUser: string;
    /** Whether a secret is stored. Never the secret. */
    apiSecretSet: boolean;
    companyCif: string;
    seriesName: string;
    vatName: string;
    vatPercentage: number;
    pricesIncludeVat: boolean;
    measuringUnit: string;
    productType: string;
    language: string;
    currency: string;
    collectType: string;
    collectSeriesName: string;
    sendEmailToClient: boolean;
    /**
     * What this deployment can actually invoice through today, built from the beans on the
     * server. Listed here rather than hardcoded in the front end so the two cannot drift --
     * adding a provider lights up the dropdown with no release on this side.
     */
    availableProviders: IAvailableProvider[];
}

/** What PUT /invoicing-settings takes. */
export interface InvoicingSettingsRequest {
    provider: string;
    enabled: boolean;
    apiUser: string;
    /** Omitted entirely to keep the stored secret; a blank string clears it. */
    apiSecret?: string;
    companyCif: string;
    seriesName: string;
    vatName: string;
    vatPercentage: number;
    pricesIncludeVat: boolean;
    measuringUnit: string;
    productType: string;
    language: string;
    currency: string;
    collectType: string;
    collectSeriesName: string;
    sendEmailToClient: boolean;
}

/** What POST /invoicing-settings/test takes. Everything falls back to what is stored. */
export interface TestConnectionRequest {
    provider?: string;
    apiUser?: string;
    apiSecret?: string;
    companyCif?: string;
}

export interface ITestedCompany {
    cif: string;
    name: string;
}

/** What the provider said about the account: the pickers, not a yes/no. */
export interface ITestConnectionResult {
    companies: ITestedCompany[];
    series: string[];
}

/**
 * What the form holds.
 *
 * `vatPercentage` is a string here where the response has a number, for the reason every
 * numeric field in this app is: an `<input>` hands back a string, and coercing it on every
 * keystroke makes a field impossible to clear -- typing over "19" would leave a 0 that
 * cannot be deleted.
 *
 * `apiSecret` starts undefined and stays that way unless somebody types in the field. That
 * absence is the whole mechanism by which saving the VAT rate does not wipe the credentials.
 */
export interface InvoicingSettingsDraft {
    provider: string;
    enabled: boolean;
    apiUser: string;
    apiSecret?: string;
    companyCif: string;
    seriesName: string;
    vatName: string;
    vatPercentage: string;
    pricesIncludeVat: boolean;
    measuringUnit: string;
    productType: string;
    language: string;
    currency: string;
    collectType: string;
    collectSeriesName: string;
    sendEmailToClient: boolean;
}

/** Not a provider: how a clinic switches invoicing off without deleting its settings. */
export const NO_PROVIDER = 'NONE';

/**
 * The one collect type a provider numbers from its own series rather than reading off a till
 * receipt, and so the only one that needs `collectSeriesName`.
 */
export const RECEIPT_COLLECT_TYPE = 'Chitanta';

/** How the money was taken, as the providers in this market name it. */
export const COLLECT_TYPES: string[] = [
    'Bon fiscal',
    RECEIPT_COLLECT_TYPE,
    'Card',
    'Ordin de plata',
];

/** What the form opens on before the first load lands. Matches the API's own defaults. */
export const defaultInvoicingSettingsDraft = (): InvoicingSettingsDraft => ({
    provider: NO_PROVIDER,
    enabled: false,
    apiUser: '',
    companyCif: '',
    seriesName: '',
    vatName: 'Normala',
    vatPercentage: '0',
    pricesIncludeVat: true,
    measuringUnit: 'buc',
    productType: 'Serviciu',
    language: 'RO',
    currency: 'RON',
    collectType: 'Bon fiscal',
    collectSeriesName: '',
    sendEmailToClient: false,
});

export const toInvoicingSettingsDraft = (
    settings: IInvoicingSettings | undefined
): InvoicingSettingsDraft =>
    settings
        ? {
              provider: settings.provider,
              enabled: settings.enabled,
              apiUser: settings.apiUser,
              // Deliberately absent: there is nothing to prefill it with, and leaving it
              // undefined is what tells the API to keep what it has.
              apiSecret: undefined,
              companyCif: settings.companyCif,
              seriesName: settings.seriesName,
              vatName: settings.vatName,
              vatPercentage: String(settings.vatPercentage),
              pricesIncludeVat: settings.pricesIncludeVat,
              measuringUnit: settings.measuringUnit,
              productType: settings.productType,
              language: settings.language,
              currency: settings.currency,
              collectType: settings.collectType,
              collectSeriesName: settings.collectSeriesName,
              sendEmailToClient: settings.sendEmailToClient,
          }
        : defaultInvoicingSettingsDraft();

export const toInvoicingSettingsRequest = (
    draft: InvoicingSettingsDraft
): InvoicingSettingsRequest => ({
    provider: draft.provider,
    enabled: draft.enabled,
    apiUser: draft.apiUser.trim(),
    // Only sent when somebody typed one. Undefined is dropped by JSON.stringify, which is
    // exactly the "leave it alone" the API is looking for.
    apiSecret: draft.apiSecret,
    companyCif: draft.companyCif.trim(),
    seriesName: draft.seriesName.trim(),
    vatName: draft.vatName.trim(),
    vatPercentage: Number(draft.vatPercentage) || 0,
    pricesIncludeVat: draft.pricesIncludeVat,
    measuringUnit: draft.measuringUnit.trim(),
    productType: draft.productType.trim(),
    language: draft.language,
    currency: draft.currency,
    collectType: draft.collectType,
    collectSeriesName: draft.collectSeriesName.trim(),
    sendEmailToClient: draft.sendEmailToClient,
});

/*
 * The rules Save is gated on live in `ClinicAdmin/invoicingSchema.ts`, stated per field so
 * each one lands on the control at fault -- sixteen inputs behind a single yes/no is what
 * made this panel's greyed-out Save unexplainable.
 *
 * Everything is only required **on the way to enabled**: a half-filled configuration left
 * switched off saves perfectly well, which is what lets an admin do this over two sittings.
 * `secretIsStored` reaches the schema through react-hook-form's `context`, because the draft
 * cannot know it -- the field is empty both when the clinic has no secret and when it has one
 * nobody has retyped.
 */
